import React, { useEffect, useState } from "react";
import { Popover } from "@mui/material";

// TODO: replace with real store listing URLs once published
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IOS_URL = "https://apps.apple.com/app/corlmi";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.corlmi";

type Platform = "ios" | "android" | "other";

const detectPlatform = (): Platform => {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as Mac; check touch points
  const isIPad =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  if (/iPhone|iPod|iPad/i.test(ua) || isIPad) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
};

type CtaEvent = "download_cta_click" | "download_store_open";

const trackCta = (event: CtaEvent, payload: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  // 1. dataLayer (GTM / GA4)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event, ...payload });
  // 2. Plausible / Fathom-style fallback
  if (typeof w.plausible === "function") {
    w.plausible(event, { props: payload });
  }
  // 3. Dev visibility
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log("[cta]", event, payload);
  }
};

const showComingSoon = () => {
  const event = new CustomEvent("corlmi:show-notification", {
    detail: {
      title: "Coming Soon",
      message: "The Corlmi mobile app is currently under development. We'll notify you as soon as it's available in the stores!",
    },
  });
  window.dispatchEvent(event);
};

const buttonClass =
  "h-14 w-full px-6 py-4 bg-white border border-[#e35157] rounded-md text-[#e35157] text-base font-semibold font-['Inter'] leading-normal hover:bg-[#fef2f2] transition-colors";

const DownloadAppButton: React.FC = () => {
  const [platform, setPlatform] = useState<Platform>("other");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const handleDirect = () => {
    if (platform === "ios") {
      trackCta("download_store_open", { store: "ios", trigger: "auto" });
      showComingSoon();
    } else if (platform === "android") {
      trackCta("download_store_open", { store: "android", trigger: "auto" });
      showComingSoon();
    }
  };

  const handlePopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackCta("download_cta_click", { platform: "desktop" });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "download-popover" : undefined;

  if (platform === "ios" || platform === "android") {
    return (
      <button type="button" className={buttonClass} onClick={handleDirect}>
        Download App
      </button>
    );
  }

  return (
    <>
      <button
        aria-describedby={id}
        type="button"
        className={buttonClass}
        onClick={handlePopoverClick}
      >
        Download App
      </button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            className: "w-56 p-2 shadow-lg rounded-md mt-2",
          }
        }}
      >
        <button
          type="button"
          onClick={() => {
            trackCta("download_store_open", {
              store: "ios",
              trigger: "manual",
            });
            handleClose();
            showComingSoon();
          }}
          className="w-full text-left px-3 py-2 rounded-sm text-sm font-medium text-[#1d2639] hover:bg-[#f7f8fa]"
        >
          App Store (iOS)
        </button>
        <button
          type="button"
          onClick={() => {
            trackCta("download_store_open", {
              store: "android",
              trigger: "manual",
            });
            handleClose();
            showComingSoon();
          }}
          className="w-full text-left px-3 py-2 rounded-sm text-sm font-medium text-[#1d2639] hover:bg-[#f7f8fa]"
        >
          Google Play (Android)
        </button>
      </Popover>
    </>
  );
};

export default DownloadAppButton;
